import React, { useState } from 'react'
import classNames from 'classnames'
import { When } from 'react-if'
import { Button, LinkButton } from '@/atoms/Button'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { ParagraphMD, ParagraphSM, TitleLG } from '@/atoms/Text'
import { Modal } from '@/molecules/Modal'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'
import {
  ModalStateType,
  ModalType,
} from '@/views/PayItForward/TheatricalPayItForwardView/sections/TheatricalPifCtaSection'
import { EmailCaptureFormInput } from '@/views/SignUpView/EmailCapture/EmailCaptureFormInput/EmailCaptureFormInput'

interface TheatricalPifEmailCollectionModalProps {
  closeModal: () => void
  isModalOpen: boolean
  modalType: ModalType
  modalState: ModalStateType
  setModalState: React.Dispatch<React.SetStateAction<ModalStateType>>
  trailer?: string
}
export const TheatricalPifEmailCollectionModal: React.FC<TheatricalPifEmailCollectionModalProps> = ({
  closeModal,
  isModalOpen,
  modalType,
  modalState,
  setModalState,
  trailer,
}) => {
  const { t } = useTranslate('pif')
  const { query } = useSafeRouter()
  const country = (query?.region as string) ?? 'us'
  const [inputValue, setInputValue] = useState('')
  return (
    <Modal
      className={classNames('z-50')}
      panelClassesOverride={`inline-block overflow-hidden transition-all transform 
      w-full sm:w-fit p-10 pt-14 align-middle text-core-gray-950 bg-white relative max-w-[425px] rounded-2xl`}
      isOpen={isModalOpen}
      onClose={() => {
        closeModal()
        setInputValue('')
      }}
      transition="appear"
    >
      <div
        className="absolute right-3 top-3 flex cursor-pointer items-center justify-center rounded-full bg-core-gray-100 p-1"
        onClick={() => {
          closeModal()
          setInputValue('')
        }}
      >
        <CloseIcon className="pointer-events-none" color="core-gray-700" size={22} />
      </div>
      <When condition={modalState === 'email'}>
        <div>
          <div className="flex flex-col gap-2 text-center">
            <TitleLG weight="bold">{t('showTimesAreComingSoonModalTitle', 'Showtimes Are Coming Soon!')}</TitleLG>
            <ParagraphSM weight="medium" color="core-gray-600">
              {t(
                'showTimesAreComingSoonModalDescription',
                'Provide your email or phone number to be among the first to know when tickets are available.',
              )}
            </ParagraphSM>
          </div>
          <EmailCaptureFormInput
            inputValue={inputValue}
            setInputValue={setInputValue}
            inputClassName="rounded-lg border border-core-gray-200 w-full py-3 px-4 mt-4 bg-[#FAFAFA] overflow-hidden"
            inputWrapperClassName="flex flex-col gap-8"
            placeholder="youremail@example.com"
            position="pif-page"
            projectSlug={query?.slug as string}
            subscriptionGroupId={`${modalType}-${country.toLowerCase()}`}
            onSubmit={() => setModalState('success')}
            customTrackPayload={{
              country,
              ticketType: modalType,
            }}
          >
            <Button disabled={inputValue.length < 4} className="mx-auto w-fit px-8 py-3" variant="black">
              {t('joinMailingListTickets', 'Join Mailing List')}
            </Button>
          </EmailCaptureFormInput>
        </div>
      </When>
      <When condition={modalState === 'success'}>
        <div className="flex flex-col gap-2 text-center">
          <TitleLG weight="bold">{t('thankYouTicketEmail', 'Thank You!')}</TitleLG>
          <ParagraphMD weight="medium" color="core-gray-600">
            {t(
              'thankYouTicketEmailDescription',
              'You’ve been added to the mailing list. We will follow up with you for updates.',
            )}
          </ParagraphMD>
        </div>
        <div className="mx-auto mt-4 flex w-full max-w-[200px] flex-col gap-3">
          <Button
            onClick={() => {
              closeModal()
              setInputValue('')
            }}
            className="px-8 py-3"
            variant="black"
          >
            {t('payItForward', 'Pay it Forward')}
          </Button>
          {trailer && (
            <LinkButton external href={trailer} outline className="px-8 py-3" variant="black">
              {t('watchTrailerTheatrical', 'Watch Trailer')}
            </LinkButton>
          )}
        </div>
      </When>
    </Modal>
  )
}
